import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { sendInput, type InputEvent } from './protocol';

interface Props {
  inputChannel: RTCDataChannel | null;
  onHide: () => void;
}

/** Imperative handle exposed to the parent so a click on the ⌨ button can call `focus()`
 *  SYNCHRONOUSLY inside the click event — mobile browsers (iOS Safari, Android Chromium,
 *  Yandex) require the `.focus()` to live in a user-gesture stack frame before they'll bring
 *  up the on-screen keyboard. A focus from `useEffect` runs after render and is silently
 *  ignored on mobile. */
export interface SoftKeyboardHandle {
  focus: () => void;
  blur: () => void;
}

/**
 * Mobile-side soft-keyboard input bridge.
 *
 * Architecture: single-path `input`-event diff against a pre-filled NBSP buffer. We deliberately
 * do NOT handle `beforeinput` with `preventDefault()` — Android Chromium / Gboard ignores the
 * cancel and fires `input` anyway with its own internally-reconciled value, so a `beforeinput`
 * handler would double-emit (once via beforeinput, once via the diff that follows). Letting the
 * default action happen natively keeps Gboard's IME tracker in sync with the DOM and there's
 * exactly one signal per user gesture: the `input` event.
 *
 * Special keys with no DOM-diff signature (Tab / Escape / Arrow*) flow through `keydown`. Enter
 * is special-cased in `beforeinput insertLineBreak` because iOS Safari's soft keyboard never
 * fires keydown for it and the `<input type="text">` strips \n from the DOM value so diff can't
 * see it either.
 */
export const SoftKeyboardOverlay = forwardRef<SoftKeyboardHandle, Props>(function SoftKeyboardOverlay(
  { inputChannel, onHide },
  ref,
) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const channelRef = useRef(inputChannel);

  useEffect(() => {
    channelRef.current = inputChannel;
  }, [inputChannel]);

  /**
   * Non-breaking-space buffer that seeds the hidden input on focus. Solely there so the very
   * first Backspace (before the user has typed anything) still fires:
   *
   *   Android Gboard / SwiftKey and iOS Safari skip the `deleteContentBackward` event entirely
   *   when the input is empty — "nothing to delete, don't bother the page". Pre-filling with
   *   8 invisible NBSPs gives the IME something to chew on so the first delete works the same
   *   as any later one. Once the user starts typing, their actual chars provide the content;
   *   we never reset on each keystroke (see onInput docstring for why that matters).
   *
   * NBSP (U+00A0) over plain space because some Android keyboards trim trailing ASCII spaces
   * from the predictive bar and would confuse our diff.
   */
  const BUFFER = '        ';

  const lastValueRef = useRef(BUFFER);

  /** Reset the hidden input back to the canonical baseline + cursor at the end. Called after
   *  every handled event so the IME's view stays consistent. */
  const resetBuffer = () => {
    const el = inputRef.current;
    if (!el) return;
    el.value = BUFFER;
    try {
      el.setSelectionRange(BUFFER.length, BUFFER.length);
    } catch {
      /* iOS Safari can throw on setSelectionRange for not-yet-focused inputs; harmless */
    }
    lastValueRef.current = BUFFER;
  };

  // Prime the buffer on mount so it's ready before the very first focus.
  useEffect(() => {
    resetBuffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        const el = inputRef.current;
        if (!el) return;
        resetBuffer();
        el.focus();
      },
      blur: () => {
        inputRef.current?.blur();
      },
    }),
    [],
  );

  const sendKey = (code: string, pressed: boolean) => {
    const ev: InputEvent = { type: pressed ? 'key_down' : 'key_up', code, modifiers: {} };
    sendInput(channelRef.current, ev);
  };

  /**
   * Send a chunk of printable text as a single `unicode_text` event. The host injects each
   * code point via `SendInput` + `KEYEVENTF_UNICODE`, so the literal glyph lands in the focused
   * window regardless of the host's active keyboard layout. This is the ONLY correct path for
   * IME input — the positional `KeyA / Digit2 / …` path only worked for ASCII on a US-layout
   * host and silently dropped Cyrillic, accented chars, emoji, and anything else outside that
   * narrow window.
   *
   * Batching matters: Gboard / Yandex / SwiftKey ship swype words (`"привет"` in one event) and
   * autocomplete picks (`"клавиатура "` with trailing space). Sending the whole `data` string in
   * one go preserves typing rhythm and avoids tearing words apart into per-char round-trips over
   * the input DataChannel.
   */
  const emitText = (text: string) => {
    if (!text) return;
    sendInput(channelRef.current, { type: 'unicode_text', text });
  };

  /**
   * Enter-only handler. iOS Safari's soft keyboard fires `insertLineBreak` here and nothing
   * else for the return key (no `keydown`, no `input` because `<input type="text">` strips \n).
   * We preventDefault to keep the DOM clean, then emit Enter. All other inputTypes pass through
   * to the default action → `input` event → diff path. Putting any other handling here causes
   * double-emit on Android Chromium, where preventDefault on beforeinput is honoured for the
   * cancellation but the `input` event still fires with Gboard's internally-tracked value.
   */
  const onBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const type = (e.nativeEvent as InputEvent2).inputType;
    if (type === 'insertLineBreak' || type === 'insertParagraph') {
      e.preventDefault();
      sendKey('Enter', true);
      sendKey('Enter', false);
    }
  };

  /**
   * Single source of truth. Diffs the current DOM value against the last known baseline by
   * code points (Array.from preserves surrogate pairs — emojis stay whole). Emits Backspaces
   * for the removed suffix and `unicode_text` for the added suffix, then snapshots the new
   * value as the next baseline.
   *
   * Crucially we DO NOT reset the DOM value to BUFFER after each event. Doing so on every
   * keystroke makes Gboard / SwiftKey think a new composition started → predictive bar resets
   * → and most importantly their internal tracker drifts: on the next keypress they reconcile
   * by re-sending the accumulated composition, which our diff sees as "added a bunch" and the
   * host re-receives chars it already has. Letting the DOM grow naturally keeps the IME tracker
   * in lockstep with reality; the hidden input is invisible (opacity 0) so unbounded growth has
   * zero UX cost. A fresh focus (`onFocus={resetBuffer}`) is the only reset point.
   *
   * Splits `addedText` on `\n` and `\t` because some Android keyboards push those through the
   * input value despite `<input type="text">` being single-line, and KEYEVENTF_UNICODE injection
   * of those control bytes doesn't reliably produce VK_RETURN / VK_TAB on Win32 apps.
   */
  const onInput = (e: React.FormEvent<HTMLInputElement>) => {
    const el = e.currentTarget;
    const current = el.value;
    const last = lastValueRef.current;
    if (current === last) return;

    const curCp = Array.from(current);
    const lastCp = Array.from(last);
    let i = 0;
    while (i < curCp.length && i < lastCp.length && curCp[i] === lastCp[i]) i++;

    const removedCount = lastCp.length - i;
    const addedCp = curCp.slice(i);

    // Deletes first, then insertions — mirrors natural typing order. A Backspace landing after
    // a unicode_text on the host would otherwise target the wrong glyph.
    for (let j = 0; j < removedCount; j++) {
      sendKey('Backspace', true);
      sendKey('Backspace', false);
    }

    let textBuf = '';
    for (const ch of addedCp) {
      if (ch === '\n' || ch === '\r') {
        if (textBuf) {
          emitText(textBuf);
          textBuf = '';
        }
        sendKey('Enter', true);
        sendKey('Enter', false);
      } else if (ch === '\t') {
        if (textBuf) {
          emitText(textBuf);
          textBuf = '';
        }
        sendKey('Tab', true);
        sendKey('Tab', false);
      } else {
        textBuf += ch;
      }
    }
    if (textBuf) emitText(textBuf);

    // Snapshot as the next baseline — NOT a full DOM reset (see docstring).
    lastValueRef.current = current;

    // If the user fully erased everything down to (and through) the BUFFER, the IME would
    // observe an empty input and skip subsequent delete events. Re-seed BUFFER so Backspace
    // remains responsive even after long mash-deletes.
    if (curCp.length === 0) {
      resetBuffer();
    }
  };

  /** Special keys with no DOM-diff signature. Enter is intentionally NOT here — beforeinput
   *  insertLineBreak is the canonical path and handling it in both places double-emits. */
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const code = e.code;
    if (!code) return;
    if (code === 'Tab' || code === 'Escape' || code.startsWith('Arrow')) {
      e.preventDefault();
      sendKey(code, true);
      sendKey(code, false);
    }
  };

  // Always render the hidden input so its DOM node is available the moment the user taps the
  // ⌨ button — focusing a not-yet-mounted element from a click handler doesn't open the soft
  // keyboard on Yandex/Chromium for Android.
  return (
    <input
      id="linkora-kbd-input"
      ref={inputRef}
      type="text"
      autoCapitalize="off"
      autoCorrect="off"
      autoComplete="off"
      spellCheck={false}
      onBeforeInput={onBeforeInput}
      onInput={onInput}
      onKeyDown={onKeyDown}
      onFocus={resetBuffer}
      onBlur={onHide}
      // `id` is set so an external `<label htmlFor="linkora-kbd-input">` (the floating ⌨
      // button) brings up the keyboard via the browser's native label-tap-to-focus pathway —
      // the only path Yandex Android / Android Chromium reliably honours. Programmatic
      // `.focus()` is silently dropped on those browsers even when called inside a synchronous
      // click handler.
      inputMode="text"
      enterKeyHint="go"
      // Sized like a real text field (60×40, 16px font) — some mobile Chromium builds
      // (incl. Yandex Android) suppress input events on inputs that look too small to be
      // interactive. `font-size: 16px` also stops iOS Safari from auto-zooming on focus,
      // which used to interrupt the IME composition stream mid-keystroke.
      //
      // `pointer-events: none` is critical: without it, the invisible 60×40 footprint sits in
      // the bottom-right of the screen — exactly where fingers land when scrubbing the touch
      // trackpad — and direct taps focus the input, popping the IME unprompted. The label
      // `htmlFor="linkora-kbd-input"` chain still focuses this input via the browser's native
      // label-tap pathway (which is independent of pointer-events on the target).
      style={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        width: '60px',
        height: '40px',
        opacity: 0,
        border: 'none',
        outline: 'none',
        padding: 0,
        margin: 0,
        background: 'transparent',
        color: 'transparent',
        caretColor: 'transparent',
        fontSize: '16px',
        pointerEvents: 'none',
      }}
      aria-label="Remote keyboard input"
    />
  );
})

/** Extends InputEvent (DOM) with the `inputType`/`data` fields that browsers expose. */
interface InputEvent2 extends Event {
  readonly inputType: string;
  readonly data: string | null;
}
